import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

export type ClientEmailAutocompleteMode = 'single' | 'list';

export type ClientEmailSuggestion = {
  id?: string | number | null;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
};

type ClientEmailAutocompleteInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onSelect'> & {
  value: string;
  onChange: (nextValue: string) => void;
  mode?: ClientEmailAutocompleteMode;
  maxSuggestions?: number;
  debounceMs?: number;
  onSelectEmail?: (suggestion: ClientEmailSuggestion) => void;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  zIndex: 50,
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  border: '1px solid #d8e1f2',
  borderRadius: 10,
  background: '#ffffff',
  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.14)',
  overflow: 'hidden',
};

const dropdownHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '8px 10px',
  borderBottom: '1px solid #e6edf7',
  color: '#52606d',
  fontSize: 12,
  fontWeight: 700,
};

const dismissButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  border: '1px solid #d8e1f2',
  borderRadius: 8,
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
};

const optionStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  borderBottom: '1px solid #eef2f7',
  background: '#ffffff',
  padding: '10px 12px',
  textAlign: 'left',
  cursor: 'pointer',
};

function toTrimmedString(value: unknown) {
  return value === null || value === undefined ? '' : String(value).trim();
}

function normalizeEmail(value: unknown) {
  return toTrimmedString(value).toLowerCase();
}

function splitListTokens(value: string) {
  return String(value || '')
    .split(/[,\n;]+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

function getTokenRange(value: string, caretIndex: number) {
  const safeCaret = Math.max(0, Math.min(caretIndex, value.length));
  const beforeCaret = value.slice(0, safeCaret);
  const afterCaret = value.slice(safeCaret);
  const lastSeparatorIndex = Math.max(
    beforeCaret.lastIndexOf(','),
    beforeCaret.lastIndexOf(';'),
    beforeCaret.lastIndexOf('\n'),
  );
  const nextSeparatorMatches = [afterCaret.indexOf(','), afterCaret.indexOf(';'), afterCaret.indexOf('\n')]
    .filter((index) => index >= 0);
  const start = lastSeparatorIndex >= 0 ? lastSeparatorIndex + 1 : 0;
  const end = nextSeparatorMatches.length > 0
    ? safeCaret + Math.min(...nextSeparatorMatches)
    : value.length;

  return {
    start,
    end,
    token: value.slice(start, end).trim(),
  };
}

export function getClientEmailSearchToken(value: string, mode: ClientEmailAutocompleteMode = 'single', caretIndex = value.length) {
  return mode === 'list'
    ? getTokenRange(value, caretIndex).token
    : value.trim();
}

export function completeClientEmailInput(
  value: string,
  selectedEmail: string,
  options: { mode?: ClientEmailAutocompleteMode; caretIndex?: number } = {},
) {
  const email = normalizeEmail(selectedEmail);
  if (!email) return value;
  if (options.mode !== 'list') return email;

  const range = getTokenRange(value, options.caretIndex ?? value.length);
  const beforeTokens = splitListTokens(value.slice(0, range.start));
  const afterTokens = splitListTokens(value.slice(range.end));
  const nextTokens: string[] = [];
  const seen = new Set<string>();

  const pushToken = (token: string) => {
    const normalized = normalizeEmail(token);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    nextTokens.push(normalized === email && EMAIL_PATTERN.test(normalized) ? normalized : token.trim());
  };

  beforeTokens.forEach(pushToken);
  pushToken(email);
  afterTokens.forEach(pushToken);

  return nextTokens.join(', ');
}

export function normalizeClientEmailSuggestion(row: unknown): ClientEmailSuggestion | null {
  const record = row && typeof row === 'object' ? row as Record<string, unknown> : {};
  const email = normalizeEmail(record.Email || record.ClientEmail);
  if (!EMAIL_PATTERN.test(email)) return null;

  return {
    id: record.ClientId as string | number | null | undefined,
    name: toTrimmedString(record.Name || record.Client || record.ClientName || record.ContactPerson),
    company: toTrimmedString(record.Company || record.ClientCompany || record.CompanyName),
    email,
    phone: toTrimmedString(record.Phone || record.ClientMobile || record.Mobile),
    address: toTrimmedString(record.Address || record.ClientAddress),
  };
}

const ClientEmailAutocompleteInput: React.FC<ClientEmailAutocompleteInputProps> = ({
  value,
  onChange,
  mode = 'single',
  maxSuggestions = 8,
  debounceMs = 220,
  onSelectEmail,
  onFocus,
  onBlur,
  onKeyDown,
  onClick,
  style,
  disabled,
  ...inputProps
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();
  const [caretIndex, setCaretIndex] = useState(value.length);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [suggestions, setSuggestions] = useState<ClientEmailSuggestion[]>([]);

  const query = useMemo(
    () => getClientEmailSearchToken(value, mode, caretIndex),
    [caretIndex, mode, value],
  );

  useEffect(() => {
    if (!open || disabled || query.length < 2) {
      setBusy(false);
      setSuggestions([]);
      setMessage('');
      return undefined;
    }

    let active = true;
    setBusy(true);
    setMessage('');

    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const matches = await azureSqlService.searchClients(query, maxSuggestions);
        if (!active) return;

        const normalized = Array.isArray(matches)
          ? matches.map(normalizeClientEmailSuggestion).filter((match): match is ClientEmailSuggestion => Boolean(match))
          : [];

        setSuggestions(normalized);
        setMessage(normalized.length > 0
          ? `Pick from ${normalized.length} client email ${normalized.length === 1 ? 'match' : 'matches'}.`
          : 'No matching client emails found.');
      } catch {
        if (!active) return;
        setSuggestions([]);
        setMessage('Client email lookup is unavailable right now.');
      } finally {
        if (active) {
          setBusy(false);
        }
      }
    }, debounceMs);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [debounceMs, disabled, maxSuggestions, open, query]);

  const updateCaretFromInput = () => {
    const nextCaret = inputRef.current?.selectionStart;
    setCaretIndex(typeof nextCaret === 'number' ? nextCaret : value.length);
  };

  const closeSuggestions = () => {
    setOpen(false);
    setBusy(false);
    setSuggestions([]);
    setMessage('');
  };

  const selectSuggestion = (suggestion: ClientEmailSuggestion) => {
    const nextValue = completeClientEmailInput(value, suggestion.email, {
      mode,
      caretIndex: inputRef.current?.selectionStart ?? caretIndex,
    });
    onChange(nextValue);
    onSelectEmail?.(suggestion);
    closeSuggestions();

    window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      const nextCaret = nextValue.length;
      input.setSelectionRange(nextCaret, nextCaret);
      setCaretIndex(nextCaret);
    });
  };

  const showDropdown = open && !disabled && (busy || suggestions.length > 0 || Boolean(message));

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        {...inputProps}
        ref={inputRef}
        value={value}
        disabled={disabled}
        style={style}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listboxId : undefined}
        onFocus={(event) => {
          setOpen(true);
          setCaretIndex(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          window.setTimeout(() => {
            if (document.activeElement !== inputRef.current) {
              closeSuggestions();
            }
          }, 120);
          onBlur?.(event);
        }}
        onClick={(event) => {
          updateCaretFromInput();
          setOpen(true);
          onClick?.(event);
        }}
        onChange={(event) => {
          const nextValue = event.currentTarget.value;
          setCaretIndex(event.currentTarget.selectionStart ?? nextValue.length);
          setOpen(true);
          onChange(nextValue);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && showDropdown) {
            event.preventDefault();
            closeSuggestions();
            return;
          }

          if (event.key === 'Enter' && showDropdown && suggestions.length > 0) {
            event.preventDefault();
            selectSuggestion(suggestions[0]);
            return;
          }

          onKeyDown?.(event);
        }}
      />
      {showDropdown ? (
        <div id={listboxId} role="listbox" style={dropdownStyle}>
          <div style={dropdownHeaderStyle}>
            <span>{busy ? 'Searching client emails...' : message || 'Pick a client email'}</span>
            <button
              type="button"
              aria-label="Close client email suggestions"
              title="Close suggestions"
              style={dismissButtonStyle}
              onMouseDown={(event) => event.preventDefault()}
              onClick={closeSuggestions}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
          {suggestions.map((suggestion) => {
            const title = suggestion.name || suggestion.company || suggestion.email;
            const subtitle = [
              suggestion.company && suggestion.company !== title ? suggestion.company : null,
              suggestion.email,
              suggestion.phone,
            ].filter(Boolean).join(' | ');
            const key = `${suggestion.id ?? title}-${suggestion.email}`;

            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={false}
                style={optionStyle}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectSuggestion(suggestion)}
              >
                <div style={{ fontSize: 13, fontWeight: 800, color: '#10243f' }}>{title}</div>
                <div style={{ marginTop: 3, fontSize: 12, color: '#52606d' }}>{subtitle}</div>
                {suggestion.address ? (
                  <div style={{ marginTop: 3, fontSize: 12, color: '#7b8794' }}>{suggestion.address}</div>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default ClientEmailAutocompleteInput;
